/**
 * Session API service
 * Implements all session-related endpoints
 */

import { apiService } from './api';
import {
  Session,
  SessionWithPlayers,
  CreateSessionRequest,
  CreateSessionResponse,
  StartSessionResponse,
  CompleteSessionResponse,
  ListSessionsResponse,
  JoinSessionRequest,
  JoinSessionResponse,
  SessionStatusResponse,
  SessionResultsResponse,
  GameStateResponse,
  MoveSessionRequest,
  MoveSessionResponse,
} from '@/types/session';

export const sessionsService = {
  /**
   * Create a new session (teacher-only)
   * POST /sessions
   */
  async createSession(
    request: CreateSessionRequest
  ): Promise<CreateSessionResponse> {
    return apiService.post<CreateSessionResponse>('/sessions', request);
  },

  /**
   * Get session details with player roster
   * GET /sessions/:sessionId
   */
  async getSession(sessionId: string): Promise<SessionWithPlayers> {
    return apiService.get<SessionWithPlayers>(`/sessions/${sessionId}`);
  },

  /**
   * Start a session (teacher-only)
   * POST /sessions/:sessionId/start
   */
  async startSession(sessionId: string): Promise<StartSessionResponse> {
    return apiService.post<StartSessionResponse>(
      `/sessions/${sessionId}/start`
    );
  },

  /**
   * Complete a session (teacher-only)
   * POST /sessions/:sessionId/complete
   */
  async completeSession(sessionId: string): Promise<CompleteSessionResponse> {
    return apiService.post<CompleteSessionResponse>(
      `/sessions/${sessionId}/complete`
    );
  },

  /**
   * List all sessions for current teacher with dashboard metrics
   * GET /sessions?classId={classId}
   * @param classId - Optional filter by classId or "uncategorized" for sessions without a class
   */
  async listSessions(classId?: string): Promise<ListSessionsResponse> {
    const params = classId ? { classId } : undefined;
    return apiService.get<ListSessionsResponse>('/sessions', params);
  },

  /**
   * Join a session as a student (unauthenticated)
   * POST /sessions/join
   */
  async joinSession(request: JoinSessionRequest): Promise<JoinSessionResponse> {
    return apiService.post<JoinSessionResponse>('/sessions/join', request);
  },

  /**
   * Get session status for students (unauthenticated)
   * GET /sessions/:sessionId/status?playerId=xxx
   */
  async getSessionStatus(sessionId: string, playerId: string): Promise<SessionStatusResponse> {
    return apiService.get<SessionStatusResponse>(`/sessions/${sessionId}/status?playerId=${playerId}`);
  },

  /**
   * Submit a decision/answer to a question (student)
   * POST /sessions/:sessionId/decisions
   */
  async submitDecision(
    sessionId: string,
    playerId: string,
    questionId: string,
    answer: any
  ): Promise<{ message: string; allAnswered: boolean; gameState: any }> {
    return apiService.post(`/sessions/${sessionId}/decisions`, {
      playerId,
      questionId,
      answer,
    });
  },

  /**
   * Remove a player from a session (student self-leave or teacher kick)
   * DELETE /sessions/:sessionId/players/:playerId
   */
  async removePlayer(sessionId: string, playerId: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/sessions/${sessionId}/players/${playerId}`, { playerId });
  },

  /**
   * Save player email (student, unauthenticated)
   * PUT /sessions/:sessionId/players/:playerId/email
   */
  async savePlayerEmail(sessionId: string, playerId: string, email: string): Promise<{ message: string }> {
    return apiService.put<{ message: string }>(`/sessions/${sessionId}/players/${playerId}/email`, { email });
  },

  /**
   * Get session results for a completed session (teacher-only)
   * GET /sessions/:sessionId/results
   */
  async getSessionResults(sessionId: string): Promise<SessionResultsResponse> {
    return apiService.get<SessionResultsResponse>(`/sessions/${sessionId}/results`);
  },

  /**
   * Simulate game - auto-play all questions for dev testing (teacher-only)
   * POST /sessions/:sessionId/simulate
   */
  async simulateGame(sessionId: string): Promise<{
    message: string;
    gameStatus: string;
    totalSubmissions: number;
    errors?: string[];
  }> {
    return apiService.post(`/sessions/${sessionId}/simulate`);
  },

  /**
   * Get game state for a live session (teacher-only)
   * GET /sessions/:sessionId/state
   */
  async getGameState(sessionId: string): Promise<GameStateResponse> {
    return apiService.get<GameStateResponse>(`/sessions/${sessionId}/state`);
  },

  /**
   * Move session to a different class folder (teacher-only)
   * PUT /sessions/:sessionId/class
   * @param sessionId - Session to move
   * @param classId - Target classId or null for uncategorized
   */
  async moveSessionToClass(sessionId: string, classId: string | null): Promise<Session> {
    const request: MoveSessionRequest = { classId };
    const response = await apiService.put<MoveSessionResponse>(`/sessions/${sessionId}/class`, request);
    return response.session;
  },
};
